import { useRef, useState } from 'react';
import { Slick } from '@/Infrastructure/slick';
import DriverDataGrid from '@components/DataGrid/Driver/Grid';
import TractorDataGrid from '@components/DataGrid/Tractor/Grid';
import { ExtendedDriver } from '@hooks/useDriverTractors.types';
import { Tractor } from '@api/Trinium/Endpoints/Tractor/types';
import { IDriverDropdownSelector } from './types';
import { GridFilterModel, useGridApiRef } from '@mui/x-data-grid-pro';

import styles from './DriverDropdown.module.css';

/**
 *	Sub-Component of the DriverDropdown for selecting Driver first followed by a Tractor when applicable
 */
export default function DriverSelector({ options, onChange, ...props }: IDriverDropdownSelector<ExtendedDriver>) {
  /** MUI Grid API Ref to forward any filters applied to the Tractor grid on to the Driver grid by default */
  const driverGridApiRef = useGridApiRef();
  /** React-Slick Ref used for programatically changing the current slide */
  const slickRef = useRef<Slick>(null);
  /** Need to retain the selected driver in the case that we also need to select a tractor */
  const [tempDriver, setTempDriver] = useState<ExtendedDriver | undefined>();

  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>();

  /** Callback when the Driver is selected. Default tractor is automatically selected unless multiple tractors are available */
  function onDriverSelect(value: ExtendedDriver | null) {
    const defaultTractor = value?.tractors.find((t) => t.id === value.tractorId);
    /** If the Driver is known to have more than one tractor allow them to select. Or if the Driver has a single tractor, but that's not his default tractor. */
    if (!props.disableSlick && value?.tractors && (value.tractors.length > 1 || (value.tractors.length === 1 && !defaultTractor))) {
      /** Slide in the Tractor Grid and allow user to select which Tractor to use */
      setTempDriver(value);
      setFilterModel(driverGridApiRef.current?.exportState().filter?.filterModel);
      slickRef.current?.slickNext();
    } else {
      /** Use the Default Tractor */
      onChange({
        driver: value ?? null,
        tractorId: value?.tractorId ?? null
      });
    }
  }

  /** Callback If Driver has multiple truck options, a truck will be selected after the driver is selected */
  function onTractorSelect(value: Tractor | null) {
    if (tempDriver && value) {
      onChange({
        driver: tempDriver,
        tractorId: value.id
      });
    }
  }

  return (
    <Slick
      className={styles.slick}
      swipe={false}
      prevArrow={<></>}
      nextArrow={<></>}
      infinite={false}
      centerPadding='0px'
      lazyLoad='ondemand'
      speed={900}
      afterChange={(index) => {
        if (index === 0) {
          setTempDriver(undefined);
        }
      }}
      ref={slickRef}>
      <div>
        <div className={styles['grid-container']}>
          <DriverDataGrid
            rows={options}
            onRowSelect={onDriverSelect}
            displayCurrentTractor
            gridApiRef={driverGridApiRef}
            legRequiresHazmat={props.legRequiresHazmatCertification}
            disableRowSelectValidation={props.disableRowSelectValidation}
            isLoading={props.isLoading}
          />
        </div>
      </div>
      <div>
        <div className={styles['grid-container']}>
          <TractorDataGrid
            key={`tractor-grid-${tempDriver?.id ?? 'unknown'}`}
            rows={tempDriver?.tractors ?? []}
            onRowSelect={onTractorSelect}
            defaultTractorId={tempDriver?.tractorId}
            defaultFilterModel={filterModel}
            legRequiresHazmat={props.legRequiresHazmatCertification}
            slickRef={slickRef}
            isLoading={props.isLoading}
          />
        </div>
      </div>
    </Slick>
  );
}
